import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getAuth } from "firebase/auth";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp, initFirebase } from "@/lib/firebase";

// API Base URL
// - Uses explicit env override when provided
// - Falls back to local API server in development
// - Falls back to current origin in production to avoid cross-origin CORS issues
const configuredApiBaseUrl = String(import.meta.env.VITE_API_BASE_URL || '').trim();
const API_BASE_URL = configuredApiBaseUrl || (import.meta.env.PROD ? '' : 'http://localhost:3001');

const FIREBASE_FUNCTIONS_REGION = String(import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'us-central1');
const USE_FIREBASE_EMULATORS = String(import.meta.env.VITE_USE_FIREBASE_EMULATORS || '').toLowerCase() === 'true';

let functionsEmulatorConnected = false;

/**
 * Get Firebase Auth ID token for authenticated requests.
 *
 * On a fresh page load, `auth.currentUser` is null for a brief window
 * while the SDK asynchronously restores the persisted session (reading
 * IndexedDB) — any request fired in that window silently goes out
 * unauthenticated and gets a 401 that nothing here retries (queryClient's
 * default `retry: false`). `authStateReady()` resolves once the SDK has
 * settled its first real auth state, so awaiting it before reading
 * `currentUser` closes that race for every caller, not just the ones that
 * happen to already gate on AuthContext's own `loading` flag.
 *
 * getIdToken() is called WITHOUT forcing a refresh — the SDK already
 * caches the current token and proactively refreshes it in the background
 * before real expiry, so a plain call returns instantly in the normal
 * case. Forcing a refresh on every single call (the previous behavior)
 * meant every page mount fired several concurrent forced-refresh calls at
 * once (this app's dashboard alone fires 4+ authenticated queries in
 * parallel) — confirmed via staging's Cloud Functions logs that some of
 * those freshly-minted tokens got rejected by the backend's verification
 * while a sibling request's slightly-older token succeeded milliseconds
 * later on the very same warm instance, consistent with a freshly-issued
 * token's `iat` claim being too close to "now" for the verifier's clock-
 * skew tolerance. A cached token doesn't have that problem.
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const auth = getAuth();
    await auth.authStateReady();
    const user = auth.currentUser;

    if (user) {
      const token = await user.getIdToken();
      return token;
    }

    return null;
  } catch (error) {
    console.warn('Failed to get Firebase auth token:', error);
    return null;
  }
}

/**
 * Determine if we should use Firebase Functions instead of Express API
 */
function shouldUseFirebaseFunctions(url: string, _method: string): boolean {
  if (/^\/api\/items\/[^/]+\/price-intelligence$/.test(url)) {
    return false;
  }

  // List of API endpoints that are backed by callable Firebase Functions.
  // /api/auth/me and /api/users/search used to be listed here, but they
  // pointed at standalone callables (getCurrentUser/searchUsers) that were
  // dead code with the wrong deployed function name to ever match this
  // file's url->functionName derivation anyway — removed 2026-08-10 along
  // with the dead functions themselves. Profile/search now go through the
  // api HTTP router instead (see shouldUseFirebaseApiRouter below).
  const firebaseFunctionEndpoints: string[] = [];

  return firebaseFunctionEndpoints.some(endpoint => url.startsWith(endpoint));
}

/**
 * Endpoints served by the HTTP `api` Firebase function router (non-callable).
 */
function shouldUseFirebaseApiRouter(url: string, _method: string): boolean {
  if (/^\/api\/items\/[^/]+\/price-intelligence$/.test(url)) {
    return true;
  }

  const routerEndpoints = [
    '/api/account',
    '/api/privacy',
    '/api/recommendations',
    '/api/price-alerts',
    '/api/price-drops',
    '/api/beneficiaries',
    '/api/wishlist-items',
    '/api/products/preview',
    '/api/creator',
    '/api/admin',
    '/api/billing',
    '/api/contacts',
    '/api/devices',
    '/api/analytics',
    '/api/mobile',
    '/api/affiliate',
    '/api/fcm',
    '/api/notifications',
    '/api/calendar',
    '/api/wishlists',
    '/api/invites',
    '/api/profile',
    '/api/users',
    '/api/shared',
    '/api/items',
    '/api/price-intelligence',
    '/api/achievements',
  ];

  return routerEndpoints.some(endpoint => url.startsWith(endpoint));
}

function normalizeBody(body: unknown): unknown {
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return body;
    }
  }
  return body;
}

function resolveFirebaseProjectId(): string {
  const fromApp = String(firebaseApp?.options?.projectId || '').trim();
  if (fromApp) {
    return fromApp;
  }

  const fromEnv = String(
    import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID_DEVELOPMENT ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID_STAGING ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID_PRODUCTION ||
    ''
  ).trim();

  if (!fromEnv) {
    throw new Error('Firebase projectId is not configured for API router requests');
  }

  return fromEnv;
}

function buildFirebaseApiRouterUrl(url: string): string {
  // In production, use relative paths that go through Firebase Hosting rewrites.
  // The hosting config rewrites /api/** to the Cloud Function named 'api'.
  // If an API base URL is configured, always prefer it (prod/dev) for non-Hosting deployments.
  
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  const apiPath = normalizedPath.startsWith('/api/')
    ? normalizedPath
    : `/api${normalizedPath}`;

  if (configuredApiBaseUrl) {
    const baseUrl = configuredApiBaseUrl.replace(/\/+$/, '');
    return `${baseUrl}${apiPath}`;
  }

  if (import.meta.env.PROD) {
    return apiPath; // Relative URL, Firebase Hosting handles the rewrite
  }

  // Local development: direct emulator URL
  const projectId = resolveFirebaseProjectId();
  const routerPath = apiPath.slice('/api'.length);
  return `http://localhost:5001/${projectId}/${FIREBASE_FUNCTIONS_REGION}/api${routerPath}`;
}

/**
 * Convert Express API endpoint to Firebase Function name
 */
type FunctionRouteMatch = {
  functionName: string;
  data: Record<string, unknown>;
};

function getFirebaseFunctionRoute(url: string, _method: string, body?: unknown): FunctionRouteMatch {
  const data = (typeof body === 'object' && body !== null && !Array.isArray(body))
    ? { ...(body as Record<string, unknown>) }
    : {};

  const patterns: Array<{ pattern: RegExp; resolve: (match: RegExpExecArray) => FunctionRouteMatch }> = [
    { pattern: /^\/api\/group-payments\/payment-intent$/, resolve: () => ({ functionName: 'groupPaymentCreateIntent', data }) },
    { pattern: /^\/api\/group-payments\/confirm$/, resolve: () => ({ functionName: 'groupPaymentConfirm', data }) },
    { pattern: /^\/api\/group-payments\/item\/([^/]+)$/, resolve: (match) => ({ functionName: 'groupGiftSummary', data: { ...data, itemId: match[1] } }) },
  ];

  for (const entry of patterns) {
    const match = entry.pattern.exec(url);
    if (match) {
      return entry.resolve(match);
    }
  }

  return {
    functionName: url.replace('/api/', '').replace(/\//g, '_'),
    data,
  };
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // For API calls, check if we got HTML instead of JSON (404 pages)
    const contentType = res.headers.get("content-type");
    if (contentType && contentType.includes("text/html")) {
      // If we got HTML, it's likely a 404 page, so provide a cleaner error
      throw new Error(`API endpoint not available (${res.status})`);
    }
    
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  url: string,
  options?: {
    method?: string;
    body?: unknown;
    useFirebaseFunctions?: boolean;
  }
): Promise<unknown> {
  const { method = "GET", body, useFirebaseFunctions } = options || {};
  const normalizedBody = normalizeBody(body);
  
  // Determine if we should use Firebase Functions
  const useFunctions = useFirebaseFunctions ?? shouldUseFirebaseFunctions(url, method);
  const useApiRouter = shouldUseFirebaseApiRouter(url, method);
  
  let fullUrl: string;
  const headers: Record<string, string> = {};
  
  if (useFunctions) {
    // Use Firebase SDK callable functions to ensure proper auth + CORS handling.
    const { functionName, data } = getFirebaseFunctionRoute(url, method, normalizedBody);

    await initFirebase({ enableAuth: true, enableFirestore: false });
    if (!firebaseApp) {
      throw new Error('Firebase app is not initialized');
    }

    const functions = getFunctions(firebaseApp, FIREBASE_FUNCTIONS_REGION);

    if (!import.meta.env.PROD && USE_FIREBASE_EMULATORS && !functionsEmulatorConnected) {
      connectFunctionsEmulator(functions, 'localhost', 5001);
      functionsEmulatorConnected = true;
    }

    const callable = httpsCallable(functions, functionName);
    const result = await callable(data);
    return result.data;
  } else if (useApiRouter) {
    await initFirebase({ enableAuth: true, enableFirestore: false });

    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fullUrl = buildFirebaseApiRouterUrl(url);
  } else {
    // Use traditional Express.js API
    fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    
    // Get Firebase Auth token for Express.js API as well (for Firebase Auth middleware)
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  if (normalizedBody) {
    headers['Content-Type'] = 'application/json';
  }
  
  const fetchOptions: RequestInit = {
    method,
    headers,
    credentials: useApiRouter ? 'omit' : 'include',
  };
  
  if (normalizedBody) {
    fetchOptions.body = JSON.stringify(normalizedBody);
  }
  
  const res = await fetch(fullUrl, fetchOptions);

  await throwIfResNotOk(res);
  
  // Check if the response is actually JSON before trying to parse it
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    const jsonResponse = await res.json();
    
    return jsonResponse;
  } else {
    // If it's not JSON, return the text or throw an error
    const text = await res.text();
    throw new Error(`API returned non-JSON response: ${text.substring(0, 200)}...`);
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn = <T,>(options: {
  on401: UnauthorizedBehavior;
  useFirebaseFunctions?: boolean;
}): QueryFunction<T> =>
  async ({ queryKey }) => {
    try {
      const url = queryKey[0] as string;

      // Use apiRequest for consistent handling of both Express API and Firebase Functions
      try {
        return await apiRequest(url, {
          method: 'GET',
          useFirebaseFunctions: options.useFirebaseFunctions
        }) as T;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        // Handle 401 errors
        if (errorMessage.includes('401')) {
          if (options.on401 === "returnNull") {
            return null as T;
          }
          throw error;
        }

        // Handle other API errors gracefully
        if (import.meta.env.DEV && !errorMessage.includes('API endpoint not available')) {
          console.warn(`API call failed for ${url}:`, error);
        }

        return (options.on401 === "returnNull" ? null : []) as T;
      }

    } catch (error) {
      // Handle network errors gracefully - only log if it's not an API availability issue
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (import.meta.env.DEV && !errorMessage.includes('API endpoint not available')) {
        console.warn(`Network error for ${queryKey[0]}:`, error);
      }
      return (options.on401 === "returnNull" ? null : []) as T;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "returnNull" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
